import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Bot, ClipboardCheck, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getPlanReviews } from "@/lib/api";
import { numberLocale } from "@/lib/money";

// Plan reviews (backend routes/plan_review.py): in place of a nutrition consult at the gym, Coach AInish
// checks each member's meal plan the moment they confirm it and sends up to three tweaks in their chat.
// No coach has to review it; this page shows what it found and sent, so coaches can follow up in chat.

const err = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;
const fmt = (n) => new Intl.NumberFormat(numberLocale()).format(Math.round(n || 0));
const ago = (iso) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso)) / 60000));
  return m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} d ago`;
};

function Stat({ label, value, target, unit = "", warn }) {
  return (
    <div className={`rounded-md border px-3 py-2 ${warn ? "border-amber-500/40 bg-amber-500/10" : "border-[#262626] bg-[#0d0d0d]"}`}>
      <div className={`text-[11px] ${warn ? "text-amber-300" : "text-zinc-500"}`}>{label}</div>
      <div className={`text-sm ${warn ? "text-amber-200" : "text-white"}`}>
        {fmt(value)}{unit}{target ? <span className="text-zinc-500"> / {fmt(target)}{unit}</span> : null}
      </div>
    </div>
  );
}

function Review({ r }) {
  const [open, setOpen] = useState(false);
  const t = r.targets || {};
  const tot = r.totals || {};
  const gaps = r.sent_gaps?.length ? r.sent_gaps : r.gaps || [];
  return (
    <li className="card-surface p-4" data-testid={`plan-review-${r.id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="font-semibold text-white">{r.name} <span className="text-xs font-normal text-zinc-500">· {r.programme || "Member"}</span></div>
        <div className="text-xs text-zinc-500">reviewed {ago(r.reviewed_at)}{r.coach_name ? ` · coach ${r.coach_name}` : ""}</div>
      </div>
      {r.totals ? (
        <div className="mt-3 grid grid-cols-3 gap-2 sm:max-w-md">
          <Stat label="Calories" value={tot.kcal} target={t.kcal} warn={t.kcal && Math.abs((tot.kcal || 0) - t.kcal) > 0.1 * t.kcal} />
          <Stat label="Protein" value={tot.protein_g} target={t.protein_g} unit=" g" warn={t.protein_g && tot.protein_g < 0.9 * t.protein_g} />
          <Stat label="Fat" value={tot.fat_g} target={t.fat_g} unit=" g" warn={t.fat_g && tot.fat_g > 1.1 * t.fat_g} />
        </div>
      ) : null}
      <div className="mt-3">
        <div className="text-xs text-zinc-500">Coach AInish spotted</div>
        {gaps.length ? (
          <ul className="mt-1 space-y-1 text-sm text-zinc-200">
            {gaps.map((g) => <li key={g} className="flex gap-1.5"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />{g}</li>)}
          </ul>
        ) : <p className="mt-1 text-sm text-emerald-300">Nothing: the plan fits their targets.</p>}
      </div>
      <div className="mt-3 rounded-md border border-[#262626] bg-[#0d0d0d] p-3">
        <div className="flex items-center gap-1.5 text-xs text-zinc-500"><Bot className="h-3.5 w-3.5" />What it sent in their chat</div>
        <p className="mt-1 whitespace-pre-line text-sm text-zinc-200">{r.message}</p>
      </div>
      {r.meals?.length ? (
        <>
          <button type="button" onClick={() => setOpen(!open)} className="mt-2 text-xs text-zinc-400 underline">
            {open ? "Hide their meals" : "See their meals"}
          </button>
          {open && (
            <div className="mt-2 grid gap-2 text-xs text-zinc-300 sm:grid-cols-2">
              {r.meals.map((m) => (
                <div key={m.label} className="rounded-md border border-[#262626] p-2">
                  <div className="text-zinc-500">{m.label}</div>
                  {m.items.length ? m.items.map((i) => <div key={i}>{i}</div>) : <div className="text-zinc-600">nothing</div>}
                </div>
              ))}
            </div>
          )}
        </>
      ) : null}
    </li>
  );
}

export default function PlanReviews() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const load = useCallback(() => getPlanReviews().then(setData).catch((e) => toast.error(err(e, "Couldn't load reviews"))), []);
  useEffect(() => { setData(null); load(); }, [load, user?.active_facility_id]);
  return (
    <div className="space-y-5 p-4 md:p-6" data-testid="plan-reviews-page">
      <div>
        <h1 className="flex items-center gap-2 font-display text-2xl font-bold text-white"><ClipboardCheck className="h-5 w-5 text-rose-400" />Plan Reviews</h1>
        <p className="mt-1 max-w-3xl text-sm text-zinc-400">
          There&apos;s no nutrition consult at the gym and no review for coaches to do: when a member confirms their meal plan, Coach AInish
          checks it against their targets and sends up to three tweaks in their chat. Here&apos;s what it found and sent; follow up in the chat if you like.
        </p>
      </div>
      {!data ? <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />
        : data.reviews.length ? <ul className="space-y-3">{data.reviews.map((r) => <Review key={r.id} r={r} />)}</ul>
          : <p className="text-sm text-zinc-500">No plans confirmed yet.</p>}
    </div>
  );
}
